'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import type { ProjectRequirements } from './ExploreSolutionsClient';

interface RequirementsPanelProps {
  requirements: ProjectRequirements;
  onChange: (req: ProjectRequirements) => void;
  onAnalyze: () => void;
  onReset: () => void;
  isAnalyzing: boolean;
  totalInputs: number;
}

const MATERIAL_OPTIONS = [
  'Aluminum', 'Titanium', 'Stainless Steel', 'Inconel', 'Carbon Fiber', 'CFRP',
  'Kevlar', 'Fiberglass', 'Copper', 'Steel', 'Cobalt Chrome',
];

const PROCESS_OPTIONS = [
  'CNC Machining', '5-Axis Milling', 'Turning', 'Sheet Metal', 'Welding',
  'TIG Welding', '3D Printing', 'DMLS', 'Composites Layup', 'Autoclave',
  'Anodizing', 'Coating', 'NDT Inspection', 'CMM Inspection',
];

const CERT_OPTIONS = ['AS9100', 'ITAR', 'NADCAP', 'ISO 17025', 'ISO 14001', 'MIL-SPEC'];

const TOLERANCE_OPTIONS = [
  { value: 'standard', label: 'Standard', sub: '±0.005"', desc: 'General purpose' },
  { value: 'precision', label: 'Precision', sub: '±0.001"', desc: 'Aerospace grade' },
  { value: 'ultra', label: 'Ultra', sub: '±0.0001"', desc: 'Mission critical' },
];

const QUANTITY_OPTIONS = [
  { value: 'prototype', label: 'Prototype', sub: '1–5 pcs' },
  { value: 'low', label: 'Low Vol.', sub: '6–100 pcs' },
  { value: 'medium', label: 'Medium', sub: '100–1K' },
  { value: 'high', label: 'High Vol.', sub: '1K+ pcs' },
];

const INDUSTRY_OPTIONS = [
  { value: 'defense', label: 'Defense', icon: 'ShieldCheckIcon', color: 'text-accent' },
  { value: 'space', label: 'Space', icon: 'GlobeAltIcon', color: 'text-mission-orange' },
  { value: 'aviation', label: 'Aviation', icon: 'PaperAirplaneIcon', color: 'text-accent' },
  { value: 'uav', label: 'UAV / Drone', icon: 'CpuChipIcon', color: 'text-aerospace-green' },
  { value: 'industrial', label: 'Industrial', icon: 'WrenchScrewdriverIcon', color: 'text-technical-gray' },
];

const SectionHeader = ({ step, label, count }: { step: number; label: string; count?: number }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className="w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0"
      style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(0,212,255,0.05))', border: '1px solid rgba(0,212,255,0.3)' }}>
      <span className="font-cta text-[10px] text-accent">{step}</span>
    </div>
    <span className="font-cta text-[11px] text-accent tracking-[0.2em]">{label}</span>
    {count !== undefined && count > 0 && (
      <span className="ml-auto font-cta text-[10px] text-accent bg-accent/10 border border-accent/20 px-2 py-0.5 rounded-full">
        {count} selected
      </span>
    )}
  </div>
);

const RequirementsPanel = ({
  requirements,
  onChange,
  onAnalyze,
  onReset,
  isAnalyzing,
  totalInputs,
}: RequirementsPanelProps) => {
  const [materialInput, setMaterialInput] = useState('');
  const [processInput, setProcessInput] = useState('');

  const toggleArrayItem = (field: 'materials' | 'processes' | 'certifications', value: string) => {
    const current = requirements[field];
    const normalized = value.toLowerCase();
    const exists = current.some((v) => v.toLowerCase() === normalized);
    onChange({
      ...requirements,
      [field]: exists ? current.filter((v) => v.toLowerCase() !== normalized) : [...current, value],
    });
  };

  const addCustom = (field: 'materials' | 'processes', value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return;
    const exists = requirements[field].some((v) => v.toLowerCase() === trimmed.toLowerCase());
    if (!exists) onChange({ ...requirements, [field]: [...requirements[field], trimmed] });
  };

  const isSelected = (field: 'materials' | 'processes' | 'certifications', value: string) =>
    requirements[field].some((v) => v.toLowerCase() === value.toLowerCase());

  const completionPct = Math.round((totalInputs / 6) * 100);

  return (
    <div className="rounded-2xl overflow-hidden sticky top-24"
      style={{
        background: 'linear-gradient(180deg, #1A2332 0%, #141C28 100%)',
        border: '1px solid rgba(0,212,255,0.15)',
        boxShadow: '0 0 40px rgba(0,0,0,0.5), inset 0 1px 0 rgba(0,212,255,0.1)',
      }}>

      {/* Panel Header */}
      <div className="px-5 py-4 border-b border-white/5 relative overflow-hidden">
        <div className="absolute inset-0 opacity-30"
          style={{ background: 'linear-gradient(90deg, rgba(0,212,255,0.05) 0%, transparent 60%)' }} />
        <div className="relative flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.2), rgba(0,212,255,0.05))', border: '1px solid rgba(0,212,255,0.3)' }}>
              <Icon name="AdjustmentsHorizontalIcon" size={16} className="text-accent" />
            </div>
            <div>
              <p className="font-cta text-[11px] text-accent tracking-[0.2em]">PROJECT REQUIREMENTS</p>
              <p className="font-body text-[11px] text-technical-gray mt-0.5">
                {totalInputs} of 6 parameters defined
              </p>
            </div>
          </div>
          {totalInputs > 0 && (
            <button onClick={onReset}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-error/20 bg-error/5 text-error font-cta text-[10px] tracking-wider hover:bg-error/10 transition-colors">
              <Icon name="ArrowPathIcon" size={11} />
              RESET
            </button>
          )}
        </div>

        {/* Progress bar */}
        <div className="mt-3 h-1 bg-white/5 rounded-full overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${completionPct}%`,
              background: 'linear-gradient(90deg, #00D4FF, #00A8CC)',
              boxShadow: completionPct > 0 ? '0 0 8px rgba(0,212,255,0.6)' : 'none',
            }}
          />
        </div>
        <div className="mt-1 flex justify-between">
          <span className="font-cta text-[9px] text-technical-gray/60 tracking-wider">COMPLETION</span>
          <span className="font-cta text-[9px] text-accent tracking-wider">{completionPct}%</span>
        </div>
      </div>

      <div className="p-5 space-y-6 max-h-[calc(100vh-220px)] overflow-y-auto"
        style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(0,212,255,0.2) transparent' }}>

        {/* ── INDUSTRY SECTOR ── */}
        <div>
          <SectionHeader step={1} label="INDUSTRY SECTOR" />
          <div className="grid grid-cols-2 gap-2">
            {INDUSTRY_OPTIONS.map((opt) => {
              const active = requirements.industry === opt.value;
              return (
                <button key={opt.value}
                  onClick={() => onChange({ ...requirements, industry: active ? '' : opt.value })}
                  className="flex items-center gap-2.5 px-3 py-3 rounded-xl border text-left transition-all duration-200 relative overflow-hidden group"
                  style={{
                    background: active ? 'rgba(0,212,255,0.08)' : 'rgba(255,255,255,0.02)',
                    borderColor: active ? 'rgba(0,212,255,0.5)' : 'rgba(255,255,255,0.07)',
                    boxShadow: active ? '0 0 12px rgba(0,212,255,0.15), inset 0 0 12px rgba(0,212,255,0.05)' : 'none',
                  }}>
                  {active && (
                    <div className="absolute inset-0 opacity-20"
                      style={{ background: 'linear-gradient(135deg, rgba(0,212,255,0.3) 0%, transparent 60%)' }} />
                  )}
                  <Icon name={opt.icon as any} size={15}
                    className={`relative z-10 transition-colors ${active ? 'text-accent' : 'text-technical-gray group-hover:text-foreground'}`} />
                  <span className={`font-body text-xs font-medium relative z-10 transition-colors ${active ? 'text-accent' : 'text-technical-gray group-hover:text-foreground'}`}>
                    {opt.label}
                  </span>
                  {active && (
                    <div className="ml-auto relative z-10 w-1.5 h-1.5 rounded-full bg-accent animate-pulse-glow" />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* ── MATERIALS ── */}
        <div>
          <SectionHeader step={2} label="MATERIALS" count={requirements.materials.length} />
          <div className="flex flex-wrap gap-1.5 mb-3">
            {MATERIAL_OPTIONS.map((m) => {
              const active = isSelected('materials', m);
              return (
                <button key={m}
                  onClick={() => toggleArrayItem('materials', m)}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-cta border transition-all duration-150"
                  style={{
                    background: active ? 'rgba(0,212,255,0.12)' : 'rgba(255,255,255,0.03)',
                    borderColor: active ? 'rgba(0,212,255,0.5)' : 'rgba(255,255,255,0.08)',
                    color: active ? '#00D4FF' : '#8B9AAB',
                    boxShadow: active ? '0 0 8px rgba(0,212,255,0.2)' : 'none',
                    letterSpacing: '0.05em',
                  }}>
                  {active && <span className="mr-1">✓</span>}{m}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <input type="text" value={materialInput}
              onChange={(e) => setMaterialInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { addCustom('materials', materialInput); setMaterialInput(''); } }}
              placeholder="Add custom material..."
              className="flex-1 rounded-lg px-3 py-2 text-xs font-body text-foreground placeholder-technical-gray/40 focus:outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(0,212,255,0.4)'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0,212,255,0.1)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
            />
            <button onClick={() => { addCustom('materials', materialInput); setMaterialInput(''); }}
              className="px-3 py-2 rounded-lg transition-colors"
              style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', color: '#00D4FF' }}>
              <Icon name="PlusIcon" size={14} />
            </button>
          </div>
          {requirements.materials.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {requirements.materials.map((m) => (
                <span key={m} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full font-cta text-[10px] tracking-wider"
                  style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.3)', color: '#00D4FF' }}>
                  {m}
                  <button onClick={() => toggleArrayItem('materials', m)} className="hover:text-error transition-colors ml-0.5">
                    <Icon name="XMarkIcon" size={9} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* ── PROCESSES ── */}
        <div>
          <SectionHeader step={3} label="MANUFACTURING PROCESSES" count={requirements.processes.length} />
          <div className="flex flex-wrap gap-1.5 mb-3">
            {PROCESS_OPTIONS.map((p) => {
              const active = isSelected('processes', p);
              return (
                <button key={p}
                  onClick={() => toggleArrayItem('processes', p)}
                  className="px-2.5 py-1.5 rounded-lg text-xs font-cta border transition-all duration-150"
                  style={{
                    background: active ? 'rgba(255,107,53,0.12)' : 'rgba(255,255,255,0.03)',
                    borderColor: active ? 'rgba(255,107,53,0.5)' : 'rgba(255,255,255,0.08)',
                    color: active ? '#FF6B35' : '#8B9AAB',
                    boxShadow: active ? '0 0 8px rgba(255,107,53,0.2)' : 'none',
                    letterSpacing: '0.05em',
                  }}>
                  {active && <span className="mr-1">✓</span>}{p}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <input type="text" value={processInput}
              onChange={(e) => setProcessInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { addCustom('processes', processInput); setProcessInput(''); } }}
              placeholder="Add custom process..."
              className="flex-1 rounded-lg px-3 py-2 text-xs font-body text-foreground placeholder-technical-gray/40 focus:outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
              onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(255,107,53,0.4)'; e.currentTarget.style.boxShadow = '0 0 0 2px rgba(255,107,53,0.1)'; }}
              onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)'; e.currentTarget.style.boxShadow = 'none'; }}
            />
            <button onClick={() => { addCustom('processes', processInput); setProcessInput(''); }}
              className="px-3 py-2 rounded-lg transition-colors"
              style={{ background: 'rgba(255,107,53,0.1)', border: '1px solid rgba(255,107,53,0.3)', color: '#FF6B35' }}>
              <Icon name="PlusIcon" size={14} />
            </button>
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* ── CERTIFICATIONS ── */}
        <div>
          <SectionHeader step={4} label="REQUIRED CERTIFICATIONS" count={requirements.certifications.length} />
          <div className="grid grid-cols-2 gap-2">
            {CERT_OPTIONS.map((c) => {
              const active = isSelected('certifications', c);
              return (
                <button key={c}
                  onClick={() => toggleArrayItem('certifications', c)}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl border transition-all duration-200 relative overflow-hidden"
                  style={{
                    background: active ? 'rgba(30,123,60,0.12)' : 'rgba(255,255,255,0.02)',
                    borderColor: active ? 'rgba(30,123,60,0.5)' : 'rgba(255,255,255,0.07)',
                    boxShadow: active ? '0 0 10px rgba(30,123,60,0.2)' : 'none',
                  }}>
                  <Icon name="ShieldCheckIcon" size={13}
                    className={active ? 'text-aerospace-green' : 'text-technical-gray'} />
                  <span className={`font-cta text-[11px] tracking-wider ${active ? 'text-aerospace-green' : 'text-technical-gray'}`}>
                    {c}
                  </span>
                  {active && <div className="ml-auto w-1.5 h-1.5 rounded-full bg-aerospace-green" />}
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* ── TOLERANCE ── */}
        <div>
          <SectionHeader step={5} label="TOLERANCE REQUIREMENT" />
          <div className="grid grid-cols-3 gap-2">
            {TOLERANCE_OPTIONS.map((opt) => {
              const active = requirements.tolerance === opt.value;
              return (
                <button key={opt.value}
                  onClick={() => onChange({ ...requirements, tolerance: active ? '' : opt.value })}
                  className="flex flex-col items-center py-3.5 px-2 rounded-xl border transition-all duration-200 relative overflow-hidden"
                  style={{
                    background: active ? 'rgba(0,212,255,0.08)' : 'rgba(255,255,255,0.02)',
                    borderColor: active ? 'rgba(0,212,255,0.5)' : 'rgba(255,255,255,0.07)',
                    boxShadow: active ? '0 0 12px rgba(0,212,255,0.15)' : 'none',
                  }}>
                  <span className={`font-body text-xs font-bold ${active ? 'text-accent' : 'text-foreground'}`}>
                    {opt.label}
                  </span>
                  <span className="font-cta text-[10px] text-accent/70 mt-0.5">{opt.sub}</span>
                  <span className="font-body text-[9px] text-technical-gray/60 mt-0.5">{opt.desc}</span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />

        {/* ── QUANTITY ── */}
        <div>
          <SectionHeader step={6} label="PRODUCTION QUANTITY" />
          <div className="grid grid-cols-2 gap-2">
            {QUANTITY_OPTIONS.map((opt) => {
              const active = requirements.quantity === opt.value;
              return (
                <button key={opt.value}
                  onClick={() => onChange({ ...requirements, quantity: active ? '' : opt.value })}
                  className="flex flex-col items-start px-3.5 py-3 rounded-xl border transition-all duration-200"
                  style={{
                    background: active ? 'rgba(0,212,255,0.08)' : 'rgba(255,255,255,0.02)',
                    borderColor: active ? 'rgba(0,212,255,0.5)' : 'rgba(255,255,255,0.07)',
                    boxShadow: active ? '0 0 12px rgba(0,212,255,0.15)' : 'none',
                  }}>
                  <span className={`font-body text-xs font-bold ${active ? 'text-accent' : 'text-foreground'}`}>
                    {opt.label}
                  </span>
                  <span className="font-cta text-[10px] text-technical-gray mt-0.5">{opt.sub}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── ANALYZE BUTTON ── */}
        <div className="pt-2">
          <button
            onClick={onAnalyze}
            disabled={totalInputs === 0 || isAnalyzing}
            className="w-full py-4 rounded-xl font-cta text-sm tracking-[0.2em] transition-all duration-300 relative overflow-hidden group"
            style={
              totalInputs > 0 && !isAnalyzing
                ? {
                    background: 'linear-gradient(135deg, #00D4FF 0%, #0099BB 100%)',
                    color: '#0B1426',
                    boxShadow: '0 0 30px rgba(0,212,255,0.4), 0 4px 20px rgba(0,0,0,0.3)',
                  }
                : {
                    background: 'rgba(255,255,255,0.05)',
                    color: '#4B5563',
                    cursor: 'not-allowed',
                    border: '1px solid rgba(255,255,255,0.08)',
                  }
            }>
            {totalInputs > 0 && !isAnalyzing && (
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                style={{ background: 'linear-gradient(135deg, #00E5FF 0%, #00B8D4 100%)' }} />
            )}
            <span className="relative z-10 flex items-center justify-center gap-2.5">
              {isAnalyzing ? (
                <>
                  <span className="w-4 h-4 border-2 border-current/30 border-t-current rounded-full animate-spin" />
                  ANALYZING REQUIREMENTS...
                </>
              ) : (
                <>
                  <Icon name="BoltIcon" size={16} />
                  MATCH CAPABILITIES
                  <Icon name="ArrowRightIcon" size={14} />
                </>
              )}
            </span>
          </button>

          {totalInputs === 0 && (
            <p className="text-center font-cta text-[10px] text-technical-gray/50 tracking-wider mt-2">
              SELECT AT LEAST ONE PARAMETER TO PROCEED
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RequirementsPanel;
